/**
 * Version history behind a compact, collapsed-by-default control.
 *
 * The editor previously rendered the full {@link VersionList} + an
 * always-visible diff panel in the sidebar at all times, eating space a
 * clinician mostly doesn't need (ADR-062). This wraps the same
 * {@link VersionList} (Load/Restore unchanged) behind a toggle button that
 * starts collapsed, plus an on-demand "Compare versions" action that hands
 * off to {@link CompareVersionsDialog} rather than rendering the diff inline.
 */
import { useState } from "react";

import VersionList from "./VersionList";
import type { VersionSummary } from "../../types";

/** Props for {@link VersionsDropdown}. */
export interface VersionsDropdownProps {
  /** Version metadata (any order; rendered newest first by VersionList). */
  versions: VersionSummary[];
  /** The currently selected "from" version number, if any. */
  fromNo: number | null;
  /** The currently selected "to" version number, if any. */
  toNo: number | null;
  /** Select a version (toggles the from/to pair the Compare dialog reads). */
  onSelect: (versionNo: number) => void;
  /** Load a version's content into the editor for editing + commit. */
  onLoad: (versionNo: number) => void;
  /** Restore a committed version as the new current version. */
  onRestore: (versionNo: number) => void;
  /** The document's current committed version number, if any. */
  currentVersionNo?: number | null;
  /** A restore-failure message to show above the list, or null. */
  restoreError?: string | null;
  /** Whether a from/to pair is selected and ready to compare. */
  compareReady: boolean;
  /** Open the on-demand Compare dialog. */
  onOpenCompare: () => void;
}

/**
 * Render the collapsed-by-default version-history control.
 *
 * @param props - The versions, selection state, and handlers.
 * @returns The dropdown element.
 */
export const VersionsDropdown: React.FC<VersionsDropdownProps> = ({
  versions,
  fromNo,
  toNo,
  onSelect,
  onLoad,
  onRestore,
  currentVersionNo,
  restoreError = null,
  compareReady,
  onOpenCompare,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="versions-dropdown-toggle"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className="h-8 px-3 rounded-md text-xs font-semibold flex items-center justify-between gap-2 border border-slate-200 text-slate-700 hover:bg-slate-50"
      >
        <span>Version history</span>
        <span className="flex items-center gap-1.5">
          <span className="rounded-full bg-slate-100 px-1.5 py-0.2 text-[10px] font-medium text-slate-500">
            {versions.length}
          </span>
          <span
            aria-hidden="true"
            className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          >
            ▾
          </span>
        </span>
      </button>
      {open && (
        <div
          data-testid="versions-dropdown-panel"
          className="absolute right-0 top-full z-30 mt-1 flex max-h-96 w-80 flex-col rounded-lg border border-slate-200 bg-white p-3 shadow-lg space-y-2"
        >
          {restoreError !== null && (
            <p
              data-testid="restore-error"
              className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1"
            >
              {restoreError}
            </p>
          )}
          <button
            type="button"
            data-testid="open-compare-versions"
            disabled={!compareReady}
            onClick={onOpenCompare}
            className="w-full shrink-0 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            {compareReady
              ? "Compare selected versions"
              : "Select two versions to compare"}
          </button>
          <div className="min-h-0 flex-1 overflow-y-auto space-y-1.5">
            <VersionList
              versions={versions}
              fromNo={fromNo}
              toNo={toNo}
              onSelect={onSelect}
              onLoad={onLoad}
              onRestore={onRestore}
              currentVersionNo={currentVersionNo}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionsDropdown;
